import { Domain as AuthDomain } from 'auth-common/domain';
import type { Tagged } from 'type-fest';
import type { AggregationRoot, MergeableEntity } from '../common';
import { SystemMutableEntity as SME } from '../common';
import type { Event } from '../event';
import { UniqueId } from '../id';
import { UserIdentifier } from '../user';

import Project = AuthDomain.Object.Project;

export type SessionIdentifier = {
  id: SessionIdentifier.Id;
  projectId: Project.Id;
  sessionId: Session.Id;
} & SessionIdentifier.Identifier;

export namespace SessionIdentifier {
  export type Id = Tagged<UniqueId, 'identifierId'>;
  export namespace Id {
    export const Generate = () => UniqueId.generate() as Id;
    export function FromString(id: string): Id {
      return UniqueId.FromString(id) as Id;
    }
  }

  export type Identifier = {
    type: string;
    value: string;
  };
  export namespace Identifier {
    export namespace Construct {
      export type Props = {
        type: string;
        value: string;
      };
    }
    export const Construct = (props: Construct.Props): Identifier => {
      return {
        type: props.type,
        value: props.value,
      };
    };

    export const Format = (identifier: Identifier): { type: string; value: string } => {
      return {
        type: identifier.type,
        value: identifier.value,
      };
    };
  }

  export namespace Construct {
    export type Props = {
      id: string;
      projectId: string;
      sessionId: string;
      type: string;
      value: string;
    };
  }
  export const Construct = (props: Construct.Props): SessionIdentifier => {
    return {
      id: Id.FromString(props.id),
      projectId: Project.Id.FromString(props.projectId),
      sessionId: Session.Id.FromString(props.sessionId),
      ...Identifier.Construct({ type: props.type, value: props.value }),
    };
  };

  export namespace Create {
    export type Props = Omit<Construct.Props, 'id'>;
  }
  export const Create = (props: Create.Props): SessionIdentifier => {
    return Construct({ id: Id.Generate(), ...props });
  };

  export const FromIdentifier = (
    projectId: Project.Id,
    sessionId: Session.Id,
    identifier: SessionIdentifier.Identifier,
  ): SessionIdentifier => {
    return {
      id: Id.Generate(),
      projectId,
      sessionId,
      ...identifier,
    };
  };
}

export type TemporaryUserIdentifier = {
  id: TemporaryUserIdentifier.Id;
  projectId: Project.Id;
  sessionId: Session.Id;
} & UserIdentifier.Identifier;

export namespace TemporaryUserIdentifier {
  export type Id = Tagged<UniqueId, 'identifierId'>;
  export namespace Id {
    export const Generate = () => UniqueId.generate() as Id;
    export function FromString(id: string): Id {
      return UniqueId.FromString(id) as Id;
    }
  }

  export namespace Construct {
    export type Props = {
      id: string;
      projectId: string;
      sessionId: string;
      type: string;
      value: string;
    };
  }
  export const Construct = (props: Construct.Props): TemporaryUserIdentifier => {
    return {
      id: Id.FromString(props.id),
      projectId: Project.Id.FromString(props.projectId),
      sessionId: Session.Id.FromString(props.sessionId),
      ...UserIdentifier.Identifier.Construct({ type: props.type, value: props.value }),
    };
  };

  export namespace Create {
    export type Props = Omit<Construct.Props, 'id'>;
  }
  export const Create = (props: Create.Props): TemporaryUserIdentifier => {
    return Construct({ id: Id.Generate(), ...props });
  };

  export const FromIdentifier = (
    projectId: Project.Id,
    sessionId: Session.Id,
    identifier: UserIdentifier.Identifier,
  ): TemporaryUserIdentifier => {
    return {
      id: Id.Generate(),
      projectId,
      sessionId,
      ...identifier,
    };
  };
}

export type Session = AggregationRoot &
  SME &
  MergeableEntity & {
    id: Session.Id;
    projectId: Project.Id;
    identifiers: SessionIdentifier[];
    temporaryUserIdentifiers: TemporaryUserIdentifier[];
  };

export namespace Session {
  export type Id = Tagged<UniqueId, 'sessionId'>;
  export namespace Id {
    export const Generate = () => UniqueId.generate() as Id;
    export function FromString(id: string): Id {
      return UniqueId.FromString(id) as Id;
    }
  }

  export namespace Construct {
    export type Props = {
      id: string;
      projectId: string;
      identifiers: { id: string; projectId: string; sessionId: string; type: string; value: string }[];
      temporaryUserIdentifiers?: { id: string; projectId: string; sessionId: string; type: string; value: string }[];
    } & SME.Props;
  }

  export const Construct = (props: Construct.Props): Session => {
    return {
      id: Id.FromString(props.id),
      projectId: Project.Id.FromString(props.projectId),
      identifiers: props.identifiers.map(SessionIdentifier.Construct),
      temporaryUserIdentifiers: props.temporaryUserIdentifiers?.map(TemporaryUserIdentifier.Construct) ?? [],
      ...SME.Construct(props),
    };
  };

  export namespace Create {
    export type Props = {
      projectId: Project.Id;
      identifiers?: { type: string; value: string }[];
      temporaryUserIdentifiers?: { type: string; value: string }[];
    };
  }

  export const Create = (props: Create.Props): Session => {
    const mutable = SME.Create();
    const id = Id.Generate();

    return {
      id,
      projectId: props.projectId,
      identifiers:
        props.identifiers?.map((identifier) => SessionIdentifier.Create({ projectId: props.projectId, sessionId: id, ...identifier })) ??
        [],
      temporaryUserIdentifiers:
        props.temporaryUserIdentifiers?.map((identifier) =>
          TemporaryUserIdentifier.Create({ projectId: props.projectId, sessionId: id, ...identifier }),
        ) ?? [],
      createdAt: mutable.createdAt,
      updatedAt: mutable.updatedAt,
    };
  };

  export const AddEventToSessions = (
    sessions: Session[],
    event: Pick<Event, 'projectId' | 'sessionIdentifiers' | 'temporaryUserIdentifiers'>,
  ): Session => {
    const oldestSession = sessions.sort((a, b) => a.createdAt.valueOf() - b.createdAt.valueOf())[0];

    const mergedSessionId = oldestSession?.id ?? Id.Generate();
    const projectId = event.projectId;
    const sessionIdentifierMap = new Map<string, SessionIdentifier>();
    const temporaryUserIdentifierMap = new Map<string, TemporaryUserIdentifier>();

    for (const session of sessions) {
      for (const identifier of session.identifiers) {
        sessionIdentifierMap.set(`${identifier.type}:${identifier.value}`, identifier);
      }
    }
    for (const identifier of event.sessionIdentifiers) {
      sessionIdentifierMap.set(
        `${identifier.type}:${identifier.value}`,
        SessionIdentifier.FromIdentifier(projectId, mergedSessionId, identifier),
      );
    }

    for (const identifier of event.temporaryUserIdentifiers ?? []) {
      temporaryUserIdentifierMap.set(
        `${identifier.type}:${identifier.value}`,
        TemporaryUserIdentifier.FromIdentifier(projectId, mergedSessionId, identifier),
      );
    }

    const mutable = oldestSession ? SME.Update({ cur: oldestSession }) : SME.Create();
    return {
      id: mergedSessionId,
      projectId,
      identifiers: Array.from(sessionIdentifierMap.values()),
      temporaryUserIdentifiers: Array.from(temporaryUserIdentifierMap.values()),
      createdAt: mutable.createdAt,
      updatedAt: mutable.updatedAt,
    };
  };
}
